import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Plus, Trash2, Loader2 } from "lucide-react";

/**
 * VO dialog — raise a new VO or edit an existing draft / revision-requested VO.
 * Supports line-item quotation (Item · Qty · Unit · Unit price) which auto-sums
 * into cost_delta. If no line items are added, falls back to a lump-sum cost.
 *
 * Props:
 *   open, onOpenChange     : dialog visibility
 *   projectId              : required when creating
 *   vo                     : existing VO (for edit mode). null for create mode.
 *   onSaved(vo)            : called after save. Passes back the fresh VO doc.
 */
const emptyLine = () => ({
  description: "",
  qty: "1",
  unit_price: "",
});

export default function VODialog({ open, onOpenChange, projectId, vo, onSaved }) {
  const editing = !!vo;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [payByDate, setPayByDate] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(vo.title || "");
      setDescription(vo.description || "");
      setPayByDate((vo.pay_by_date || "").slice(0, 10));
      const li = (vo.line_items || []).map((l) => ({
        description: l.description || "",
        qty: String(l.qty ?? ""),
        unit_price: String(l.unit_price ?? ""),
      }));
      setLines(li.length > 0 ? li : [emptyLine()]);
    } else {
      setTitle("");
      setDescription("");
      setPayByDate("");
      setLines([emptyLine()]);
    }
  }, [open, editing, vo]);

  const total = useMemo(() => {
    return lines.reduce((s, l) => {
      const q = parseFloat(l.qty) || 0;
      const p = parseFloat(l.unit_price) || 0;
      return s + q * p;
    }, 0);
  }, [lines]);

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));
  const patchLine = (i, k, v) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const save = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    const line_items = lines
      .filter((l) => l.description.trim())
      .map((l, idx) => ({
        item_code: `#${idx + 1}`,
        description: l.description.trim(),
        qty: parseFloat(l.qty) || 0,
        unit: null,
        unit_price: parseFloat(l.unit_price) || 0,
      }));
    if (line_items.length === 0) {
      toast.error("Add at least one line item with a description");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        line_items,
        pay_by_date: payByDate || null,
      };
      let r;
      if (editing) {
        r = await api.patch(`/vos/${vo.vo_id}`, payload);
      } else {
        r = await api.post(`/projects/${projectId}/vos`, payload);
      }
      toast.success(editing ? "VO updated" : "VO drafted");
      onSaved && (await onSaved(r.data.vo));
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="vo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2C2A29]">
            {editing ? "Edit variation order" : "New variation order"}
          </DialogTitle>
          <DialogDescription className="text-[#7A7571]">
            Draft the change, attach the quotation & supporting files, then send to the homeowner for approval.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4">
          <input
            className="input-soft"
            placeholder="Title (e.g., Extra kitchen island)"
            required
            data-testid="vo-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            className="input-soft min-h-[90px]"
            placeholder="Describe the change (plain language for the homeowner)"
            required
            data-testid="vo-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Pay-by date — becomes the due_date on the auto-created payment
              stage the moment the homeowner approves this VO. */}
          <div className="rounded-2xl border border-[#EBE5D9] bg-[#FBF7EF]/60 p-3">
            <label className="block text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2">
              Pay by date
            </label>
            <input
              type="date"
              className="input-soft"
              data-testid="vo-pay-by-date"
              value={payByDate}
              onChange={(e) => setPayByDate(e.target.value)}
            />
            <p className="text-[11px] text-[#7A7571] mt-2 leading-snug">
              When the homeowner approves this VO, a payment stage will be created automatically with this date as its due date.
            </p>
          </div>

          {/* Line items table */}
          <div className="rounded-2xl border border-[#EBE5D9] bg-[#FBF7EF]/60 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">
                Quotation
                {lines.length > 0 && <span className="ml-2 text-[#2C2A29] normal-case tracking-normal">{lines.length} item{lines.length === 1 ? "" : "s"}</span>}
              </div>
              <button
                type="button"
                onClick={addLine}
                data-testid="vo-add-line"
                className="btn-ghost text-xs inline-flex items-center gap-1"
              >
                <Plus size={12} /> Add line item
              </button>
            </div>

            <div className="space-y-1.5">
              {/* Header row (desktop only) */}
              <div className="hidden md:grid grid-cols-12 gap-2 px-1 text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">
                <div className="col-span-1">#</div>
                <div className="col-span-5">Description</div>
                <div className="col-span-2">Qty</div>
                <div className="col-span-2">Unit price</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-1"></div>
              </div>
              {lines.map((l, i) => {
                const q = parseFloat(l.qty) || 0;
                const up = parseFloat(l.unit_price) || 0;
                const lt = q * up;
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white rounded-xl p-2 border border-[#EBE5D9]" data-testid={`vo-line-${i}`}>
                    <div className="col-span-1 text-sm font-mono font-semibold text-[#7A7571] text-center">
                      #{i + 1}
                    </div>
                    <input
                      className="input-soft col-span-11 md:col-span-5 !py-2 text-xs"
                      placeholder="Description"
                      required
                      value={l.description}
                      onChange={(e) => patchLine(i, "description", e.target.value)}
                      data-testid={`vo-line-desc-${i}`}
                    />
                    <input
                      className="input-soft col-span-4 md:col-span-2 !py-2 text-xs"
                      type="number"
                      step="0.01"
                      placeholder="Qty"
                      value={l.qty}
                      onChange={(e) => patchLine(i, "qty", e.target.value)}
                      data-testid={`vo-line-qty-${i}`}
                    />
                    <input
                      className="input-soft col-span-5 md:col-span-2 !py-2 text-xs"
                      type="number"
                      step="0.01"
                      placeholder="Unit price"
                      value={l.unit_price}
                      onChange={(e) => patchLine(i, "unit_price", e.target.value)}
                      data-testid={`vo-line-price-${i}`}
                    />
                    <div className="col-span-2 md:col-span-1 text-right text-xs font-semibold text-[#2C2A29]">
                      ${lt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      disabled={lines.length === 1}
                      className="col-span-1 btn-ghost !p-1.5 text-[#9F1239] justify-self-end disabled:opacity-40 disabled:cursor-not-allowed"
                      title={lines.length === 1 ? "At least one line item required" : "Remove"}
                      data-testid={`vo-line-remove-${i}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-2xl bg-[#F7F4EC]">
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Total Cost</div>
            <div className="text-2xl font-display font-bold text-[#E07A5F]" data-testid="vo-total">
              +${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="btn-ghost">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="vo-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {editing ? "Save changes" : "Save as draft"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
